// frontend/src/services/socket.js
//
// ⚠️ Le singleton doit renvoyer la connexion existante dès qu'elle EXISTE,
// pas seulement quand elle est déjà `.connected`. Avec l'ancien garde-fou
// (`socket?.connected`), toute page qui monte PLUSIEURS composants abonnés en
// même temps (ex: une liste de N AssociationCard, chacune avec son propre
// useEffect qui appelle connectSocket()) créait N connexions Socket.io en
// parallèle — le handshake réseau prend quelques dizaines/centaines de ms,
// donc le 2e, 3e... appelant voyait `.connected === false` et recréait une
// connexion au lieu de réutiliser celle en cours d'établissement. Résultat :
// plusieurs sockets orphelins, et joinAssociation()/onAssociationUpdate() de
// chaque carte opérant sur "la référence courante" plutôt que sur LEUR propre
// connexion — la plupart des abonnés ne recevaient jamais rien.
//
// Un écran avec un seul abonné (ex: Dashboard Simple, un seul composant) ne
// déclenchait jamais cette course — c'est pour ça qu'il "marchait" alors que
// la liste Owner ("Mes Associations", plusieurs cartes) ne marchait pas.

import { io } from 'socket.io-client';

let socket = null;
let connecting = null;

// ✅ Rooms suivies — rejointes à CHAQUE connexion (première connexion ET
//    reconnexions après coupure réseau), pas seulement au moment de l'appel
//    de joinAssociation(). Évite de dépendre de l'ordre d'exécution entre
//    "socket connecté" et "composant qui demande à rejoindre sa room".
const joinedRooms = new Set();

function rejoinAllRooms() {
  if (!socket?.connected) return;
  joinedRooms.forEach((id) => socket.emit('join:association', id));
}

/**
 * Initialiser (ou réutiliser) la connexion Socket.io.
 * Toujours la MÊME instance tant qu'elle n'a pas été explicitement fermée —
 * qu'elle soit déjà connectée ou en cours de connexion/reconnexion.
 */
export function connectSocket() {
  if (socket) return socket;

  const token = localStorage.getItem('token');
  if (!token) return null;

  // ✅ Utiliser la même base URL que l'API (sans /api)
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const baseUrl = apiUrl.replace('/api', '');

  socket = io(baseUrl, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionAttempts: 10,
  });

  // ✅ Rejoindre toutes les rooms suivies à chaque connexion ET reconnexion —
  //    c'est ce qui garantit qu'une carte qui a appelé joinAssociation()
  //    AVANT que le handshake soit terminé ne perd jamais sa demande.
  socket.on('connect', () => {
    console.log('🔌 Socket connecté');
    rejoinAllRooms();
  });

  socket.on('disconnect', (reason) => {
    console.log('🔌 Socket déconnecté:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('❌ Socket erreur:', error.message);
  });

  return socket;
}

/**
 * Rejoindre la room d'une association. Peut être appelé avant même que la
 * connexion soit établie : la room est mémorisée et rejointe automatiquement
 * dès que le socket se (re)connecte.
 */
export function joinAssociation(associationId) {
  if (!associationId) return;
  joinedRooms.add(associationId);
  if (socket?.connected) {
    socket.emit('join:association', associationId);
  }
}

export function leaveAssociation(associationId) {
  joinedRooms.delete(associationId);
}

/**
 * Écouter les mises à jour d'une association
 * @param {Function} callback - (data) => { associationId, autoMode, switchState, ... }
 * @returns {Function} cleanup function
 */
export function onAssociationUpdate(callback) {
  if (!socket) return () => {};

  socket.on('association:updated', callback);
  return () => socket.off('association:updated', callback);
}

/**
 * Écouter les contrôles manuels
 */
export function onAssociationControl(callback) {
  if (!socket) return () => {};

  socket.on('association:controlled', callback);
  return () => socket.off('association:controlled', callback);
}

/**
 * Écouter les actions d'automation
 */
export function onAutomationAction(callback) {
  if (!socket) return () => {};

  socket.on('association:automation', callback);
  return () => socket.off('association:automation', callback);
}

/**
 * Déconnecter le socket
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  joinedRooms.clear();
}

export function getSocket() {
  return socket;
}
